import { SafePromise, SafeType } from '@benzinga/safe-await';
import { oneRequestAtATime } from '@benzinga/utils';
import { Subscribable, Subscription } from '@benzinga/subscribable';

import { Session } from '@benzinga/session';
import { PlusProduct, TradeAlertProductData, TradeAlertVideos } from './entities';
import { PlusRequest, PlusRequestEvent } from './request';
import { PlusStore, PlusStoreEvent } from './store';
import { PermissionsManager } from '@benzinga/permission-manager';

export type PlusManagerEvent = PlusRequestEvent | PlusStoreEvent;

export class PlusManager extends Subscribable<PlusManagerEvent> {
  private store: PlusStore;
  private request: PlusRequest;
  private storeSubscription?: Subscription<PlusStore>;
  private requestSubscription?: Subscription<PlusRequest>;

  private fetchProduct = oneRequestAtATime(async () => {
    const products = await this.request.getProducts();
    if (products.err) {
      return products as SafeType<PlusProduct[] | undefined>;
    } else {
      this.store.updateProducts(products.ok);
      return products;
    }
  });

  constructor(session: Session) {
    super();
    this.request = new PlusRequest(session);
    this.store = new PlusStore();
    const permissionsManager = session.getManager(PermissionsManager);
    const permissionsSubscription = permissionsManager.subscribe(event => {
      switch (event.type) {
        case 'permission_changed':
          this.fetchProduct();
          break;
      }
    });
    session.onPreSessionClose(() => permissionsSubscription.unsubscribe());
    this.fetchProduct();
  }

  public static getName = () => 'benzinga-plus';

  // avoid using this.
  public getStoredProducts = (): PlusProduct[] | undefined => {
    return this.store.getProducts();
  };

  public getProducts = async (force = false): SafePromise<PlusProduct[] | undefined> => {
    if (force || this.store.shouldWeFetchProduct() === true) {
      return await this.fetchProduct();
    } else {
      return { ok: this.store.getProducts() };
    }
  };

  public getProductData = async (product: string): SafePromise<TradeAlertProductData> => {
    return await this.request.getProductData(product);
  };

  public getProductVideos = async (product: string): SafePromise<TradeAlertVideos> => {
    return await this.request.getProductVideos(product);
  };

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
    this.storeSubscription = this.store.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
  }
}
